import { useState, useCallback } from 'react';
import { useFetch } from './useFetch';
import { API_CONFIG } from '../config/api';

interface Notification {
  id: string;
  title: string;
  message: string;
  imageUrl: string | null;
  createdAt: string;
}

interface NotificationState {
  notifications: Notification[];
  isLoading: boolean;
  error: string | null;
}

export const useNotifications = () => {
  const [notificationState, setNotificationState] = useState<NotificationState>({
    notifications: [],
    isLoading: false,
    error: null,
  });

  const { fetchWithAuth } = useFetch();

  const fetchNotifications = useCallback(async (): Promise<void> => {
    setNotificationState(prev => ({
      ...prev,
      isLoading: true,
      error: null,
    }));

    try {
      const response = await fetchWithAuth(`${API_CONFIG.BASE_URL}/notifications`);

      // Se for 404 ou 500, considerar como lista vazia (sem erro)
      if (!response.ok) {
        if (response.status === 404 || response.status === 500) {
          setNotificationState(prev => ({
            ...prev,
            notifications: [],
            isLoading: false,
            error: null,
          }));
          return;
        }
        
        const errorText = await response.text();
        throw new Error(`Erro ao buscar notificações: ${response.status} - ${errorText}`);
      }

      const data = await response.json();

      // A API retorna {notifications: Array}, então extraímos o array
      const notificationsData = data.notifications || [];

      setNotificationState(prev => ({
        ...prev,
        notifications: notificationsData,
        isLoading: false,
      }));
    } catch (error) {
      setNotificationState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Erro desconhecido',
        isLoading: false,
      }));
    }
  }, [fetchWithAuth]);

  const resetNotificationState = () => {
    setNotificationState({
      notifications: [],
      isLoading: false,
      error: null,
    });
  };

  const hasRecentNotifications = useCallback((): boolean => {
    if (notificationState.notifications.length === 0) return false;
    
    const now = new Date();
    return notificationState.notifications.some((notification) => {
      const notificationDate = new Date(notification.createdAt);
      const timeDifference = now.getTime() - notificationDate.getTime();
      const hoursDifference = timeDifference / (1000 * 60 * 60);
      return hoursDifference <= 24;
    });
  }, [notificationState.notifications]);

  return {
    fetchNotifications,
    notificationState,
    resetNotificationState,
    hasRecentNotifications,
  };
};
